import { type numeric } from '@core/type/numeric'
import { SYMBOLS } from './constant.util'

export const IS_UNDEFINED = (v: any): v is undefined => typeof v === 'undefined'

export const IS_DEFINED = <T> (v: T | undefined): v is T => typeof v !== 'undefined'

export const IS_NULL = (v: any): v is null => v === null

export const IS_NOT_NULL = <T> (v: T | null): v is T => v !== null

export const IS_SET = <T> (v: T | null | undefined): v is T => (v !== null) && (typeof v !== 'undefined')

export const IS_NOT_SET = (v: any): v is null | undefined => (v === null) || (typeof v === 'undefined')

export const IS_NUMERIC = (v: any): v is numeric => (
  ((typeof v === 'number') && !Number.isNaN(v)) ||
  ((typeof v === 'string') && (v !== '') && !Number.isNaN(Number.parseFloat(v)))
)

export const IS_NOT_NUMERIC = (v: any): boolean => {
  const bNumber = typeof v === 'number'
  const bString = typeof v === 'string'

  return (
    (!bNumber && !bString) ||
    (bNumber && Number.isNaN(v)) ||
    (v === '') ||
    (
      bString && (
        (Number.isNaN(Number(v))) ||
        (Number.isNaN(Number.parseFloat(v)))
      )
    )
  )
}

export const IS_A_NUMBER = (v: any): v is number => (typeof v === 'number') && !Number.isNaN(v)

export const IS_NOT_A_NUMBER = (v: any): boolean => (typeof v !== 'number') || Number.isNaN(v)

export const IS_A_STRING = (v: any): v is string => typeof v === 'string'

export const IS_A_STRING_AND_EMPTY = (v: any): v is '' => v === ''

export const IS_A_STRING_AND_NOT_EMPTY = (v: any): v is string => (typeof v === 'string') && (v !== '')

export const IS_AN_OBJECT = (o: any): o is any => (o !== null) && (typeof o === 'object')

export const IS_AN_OBJECT_AND_EMPTY = (o: any): o is any => (
  (o !== null) && (typeof o === 'object') && (Object.keys(o as Record<string, any>).length === 0)
)

export const IS_AN_OBJECT_AND_NOT_EMPTY = (o: any): o is any => (
  (o !== null) && (typeof o === 'object') && (Object.keys(o as Record<string, any>).length !== 0)
)

export const IS_AN_ARRAY = (v: any): v is any[] => Array.isArray(v)

export const IS_AN_ARRAY_AND_EMPTY = (v: any): v is [] => Array.isArray(v) && (v.length === 0)

export const IS_AN_ARRAY_AND_NOT_EMPTY = (v: any): v is any[] => Array.isArray(v) && (v.length !== 0)

export const IS_A_SET = (o: any): o is Set<any> => o instanceof Set

export const IS_A_SET_AND_NOT_EMPTY = (o: any): o is Set<any> => (o instanceof Set) && (o.size !== 0)

export const IS_A_SET_AND_EMPTY = (o: any): o is Set<any> => (o instanceof Set) && (o.size === 0)

export const IS_A_MAP = (o: any): o is Map<any, any> => o instanceof Map

export const IS_A_MAP_AND_NOT_EMPTY = (o: any): o is Map<any, any> => (o instanceof Map) && (o.size !== 0)

export const IS_A_MAP_AND_EMPTY = (o: any): o is Map<any, any> => (o instanceof Map) && (o.size === 0)

export const IS_A_FUNCTION = (o: any): o is ((..._: any) => any) => typeof o === 'function'

export const IS_A_BOOLEAN = (v: any): v is boolean => typeof v === 'boolean'

export const IS_A_DATE_AND_NOT_EMPTY = (o: any): boolean => (o instanceof Date) && ((+o - +o) === 0)

export const IS_A_DATE_AND_EMPTY = (o: any): boolean => (o instanceof Date) && ((+o - +o) !== 0)

export const IS_EMPTY = (v: any): boolean => (
  (typeof v === 'undefined') ||
  (v === null) ||
  (v === '') ||
  (Array.isArray(v) && (v.length === 0)) ||
  ((v instanceof Set) && (v.size === 0)) ||
  ((v instanceof Map) && (v.size === 0)) ||
  ((v instanceof Date) && ((+v) - (+v)) !== 0) ||
  ((typeof v === 'object') && (Object.keys(v as Record<string, any>).length === 0))
)

export const IS_NOT_EMPTY = <T> (_: T | null | undefined): _ is T => !IS_EMPTY(_)

export const IS_OFF = (v: any): boolean => (
  (typeof v === 'undefined') ||
  (v === null) ||
  (v === false) ||
  (v === '') ||
  (v === 0) ||
  (v === 0n) ||
  ((typeof v === 'number') && isNaN(v))
)
export const IS_ON = (v: any): boolean => !IS_OFF(v)

export const IS_EVEN = (v: any): boolean => {
  let res = false
  if (IS_NUMERIC(v)) {
    res = (+v % 2) === 0
  } else if (IS_SET(v) && !IS_EMPTY(v)) {
    const _v = `${v as string}`

    res = _v.length > 0

    if (res) {
      res = (SYMBOLS.indexOf(_v[_v.length - 1]) % 2) === 0
    }
  }
  return res
}
export const IS_ODD = (v: any): boolean => {
  let res = false

  if (IS_NUMERIC(v)) {
    res = (+v % 2) === 1
  } else if (IS_SET(v) && !IS_EMPTY(v)) {
    const _v = `${v as string}`

    res = _v.length > 0

    if (res) {
      res = (SYMBOLS.indexOf(_v[_v.length - 1]) % 2) === 1
    }
  }
  return res
}

export const IS_NUMERIC_AND_SAFE = (v: any): v is numeric => {
  const x = Number.parseFloat(`${v as string}`)

  let bNumericAndSafe = x === 0

  if (!bNumericAndSafe && !Number.isNaN(x)) {
    bNumericAndSafe = (
      ((Number.MIN_SAFE_INTEGER <= x) && (x <= -Number.EPSILON)) ||
      ((Number.EPSILON <= x) && (x <= Number.MAX_SAFE_INTEGER))
    )
  }

  return bNumericAndSafe
}

export const IS_NUMERIC_AND_UNSAFE = (v: any): boolean => !IS_NUMERIC_AND_SAFE(v)

export const REGEXP_NO_WHITESPACE = /^\S*$/g

export const REGEXP_NO_WHITESPACE_STRING = '^\\S*$'

export const REGEX_EMAIL = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/g

export const REGEX_EMAIL_STRING = "^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$"

export const IS_A_PUBLIC_URL = (rawUrl?: string): boolean => {
  const url = `${rawUrl ?? ''}`
  // allow paths:
  const publicPathWhitelist = [
    '/account/token'
  ]
  return (
    // here we hardcode that root ( / ) and ( /public followed by _ or / ) paths are public
    ['', '/', '/public_', '/public/'].includes(url.substring(0, 7)) ||
    publicPathWhitelist.some(path => url.split(/[#?]/)[0] === path)
  )
}

export const CHECK = (_: { that: boolean, orThrow?: any }): boolean => {
  if (IS_SET(_.orThrow) && !_.that) {
    throw _.orThrow as Error
  }
  return _.that
}

export const IS_KEY_OF_OBJECT = (_: { key: string | number, of?: any }): boolean => {
  return IS_AN_OBJECT_AND_NOT_EMPTY(_.of) && Object.keys(_.of as Record<string, any>).includes(`${_.key}`)
}
